import { useEffect, useState } from "react";

/**
 * NewsLoader — news-specific waiting animation.
 *
 * The weather dashboard has ForecastLoader which walks through the
 * 6-model ensemble → AI narrative → digest render. Users stared at
 * it while the news tab was loading and reasonably asked "why are we
 * pulling weather models for news?". This component tells the real
 * story: Perplexity scan → URL validation (the whole 404-prevention
 * pipeline) → cross-reference → digest render.
 *
 * Stages mirror what the backend actually does so the loader doubles
 * as a silent product-trust message: "we check every link before we
 * send it to you."
 */
import { Newspaper, Link2, ShieldCheck, FileText } from "lucide-react";

const STAGES = [
  { icon: Newspaper, title: "Scanning newsrooms", detail: "Pulling fresh coverage matching your interests" },
  { icon: Link2, title: "Checking every link", detail: "We verify each URL resolves before sending" },
  { icon: ShieldCheck, title: "Filtering paywalls & soft-404s", detail: "Only free, reachable articles make the cut" },
  { icon: FileText, title: "Curating your digest", detail: "Wrapping six stories for your inbox and dashboard" },
];

export default function NewsLoader({ label = "Building your news digest" }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setStage((s) => (s + 1) % STAGES.length);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center py-16 px-6 text-center"
      data-testid="news-loader"
    >
      <div className="ticker-label mb-3 text-neutral-600">{label}</div>
      <div className="relative w-full max-w-md">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const active = i === stage;
          return (
            <div
              key={i}
              className={`flex items-start gap-4 p-4 border-2 border-black transition-opacity duration-500 ${
                active ? "opacity-100 bg-[#FFFBE6]" : "opacity-40 bg-white"
              } ${i > 0 ? "-mt-[2px]" : ""}`}
              data-testid={`news-loader-stage-${i}`}
              data-active={active}
            >
              <div
                className={`flex items-center justify-center w-10 h-10 border-2 border-black shrink-0 ${
                  active ? "bg-black text-[#FFD600] animate-pulse" : "bg-white text-black"
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <div className="font-display text-sm font-black tracking-tight leading-tight">{s.title}</div>
                <div className="text-xs text-neutral-600 mt-0.5 leading-tight">{s.detail}</div>
              </div>
              {active && (
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0A0A0A] self-center shrink-0">
                  …
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-neutral-500 mt-5 max-w-xs">
        First load takes ~30-45s because we verify every link. After that, your digest loads instantly.
      </p>
    </div>
  );
}
